import { AccessTime, Cancel, CheckCircleOutline, DoNotDisturb, KeyboardArrowDown } from '@mui/icons-material'
import { Box, Chip, Menu, MenuItem, Typography } from '@mui/material'
import { ProfileImage } from '../../../customComponents/user/ProfileImage'
import { ActionStatus, type CETAPlanAction } from '../../../../models/CETAPlanSectionModel'
import { useRef, useState } from 'react'
import { UpdateActionStatus } from '../../../../services/CETAClassroomService'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

interface Props {
  readOnly: boolean
  action: CETAPlanAction
  index?: number
  showStatus?: boolean
  deleteAction?: (index: number) => void
  onUpdateStatus?: () => void
}

export const CETAGoalActionDetails = (props: Props): JSX.Element => {
  const statusChip = useRef<HTMLDivElement | null>(null)

  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false)

  const handleDelete = (): void => {
    if (props.deleteAction != null && props.index != null) {
      props.deleteAction(props.index)
    }
  }

  const handleUpdateStatus = async (status: ActionStatus): Promise<void> => {
    setIsStatusMenuOpen(false)
    await UpdateActionStatus(props.action.id ?? 0, status)
    if (props.onUpdateStatus != null) {
      props.onUpdateStatus()
    }
  }

  let statusIcon = <></>
  let statusLabel = ''
  if (props.action.status === ActionStatus.NotStarted) {
    statusIcon = <DoNotDisturb sx={{ color: 'var(--grey-700) !important' }} />
    statusLabel = 'Not Started'
  } else if (props.action.status === ActionStatus.InProgress) {
    statusIcon = <AccessTime sx={{ color: 'var(--grey-700) !important' }} />
    statusLabel = 'In Progress'
  } else if (props.action.status === ActionStatus.OnHold) {
    statusIcon = <ErrorOutlineIcon sx={{ color: 'var(--grey-700) !important' }} />
    statusLabel = 'On Hold'
  } else if (props.action.status === ActionStatus.Complete) {
    statusIcon = <CheckCircleOutline sx={{ color: 'var(--grey-700) !important' }} />
    statusLabel = 'Complete'
  }

  return <Box display='flex' alignItems='center' justifyContent='space-between' mb='1em' data-testid='actionDetails'>
    <Box display='flex'>
      <ProfileImage name={props.action.owner.name} imageKey={props.action.owner.imageKey} />

      <Box ml='.5em'>
        <Box display='flex'>
          <Typography variant='body1'>{props.action.owner.name}</Typography>
          <Typography variant='body1' ml='1em'>{new Date(props.action.targetDate).toLocaleDateString()}</Typography>
        </Box>

        <Typography variant='body2'>{props.action.summary}</Typography>
      </Box>

      {props.showStatus === true &&
        <Chip
          ref={statusChip}
          icon={statusIcon}
          data-testid='actionStatus'
          label={
            <Box display='flex' alignItems='center'>
              {statusLabel}

              {props.onUpdateStatus != null && !props.readOnly &&
                <KeyboardArrowDown sx={{ ml: '.25em' }} data-testid='actionStatus-clickable' />
              }
            </Box>
          }
          sx={{ ml: '1em', backgroundColor: 'white', border: '1px solid var(--border)' }}
          onClick={props.onUpdateStatus != null && !props.readOnly ? () => { setIsStatusMenuOpen(true) } : undefined}
        />
      }

      <Menu open={isStatusMenuOpen} anchorEl={statusChip.current} onClose={() => { setIsStatusMenuOpen(false) }}>
        <MenuItem
          data-testid='notStarted'
          onClick={async () => { await handleUpdateStatus(ActionStatus.NotStarted) }}
        >
          Not Started
        </MenuItem>

        <MenuItem
          data-testid='inProgress'
          onClick={async () => { await handleUpdateStatus(ActionStatus.InProgress) }}
        >
          In Progress
        </MenuItem>

        <MenuItem
          data-testid='onHold'
          onClick={async () => { await handleUpdateStatus(ActionStatus.OnHold) }}
        >
          On Hold
        </MenuItem>

        <MenuItem
          data-testid='complete'
          onClick={async () => { await handleUpdateStatus(ActionStatus.Complete) }}
        >
          Complete
        </MenuItem>
      </Menu>
    </Box>

    {props.deleteAction != null && !props.readOnly &&
      <Cancel
        sx={{ width: '16px', height: '16px', ml: '1em', color: 'var(--red-500)', cursor: 'pointer' }}
        onClick={handleDelete}
        data-testid='deleteAction'
      />
    }
  </Box>
}
